import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Settings } from "lucide-react";
import toast from "react-hot-toast";
import i18n from "../../../i18n";
import { serverErrorMessage } from "../../../utils/serverError";
import AreaDetail from "./AreaDetail";
import AreaRow from "./AreaRow";
import { Badge, Button } from "./atoms";
import CheckInModal from "./CheckInModal";
import ConcentricRingsChart from "./ConcentricRingsChart";
import InsightPanel from "./InsightPanel";
import { LifeAreasSkeleton } from "./LoadingSkeleton";
import { LifeAreasProvider, useLifeAreas } from "./config";
import { compositeOf, timeAgo } from "./lifeAreasTheme";
import NextSteps from "./NextSteps";
import {
  actOnStep,
  flushQueue,
  getConfig,
  getInsights,
  getNextSteps,
  getSnapshot,
  hasQueuedCheckIns,
} from "./ringsApi";
import SettingsModal from "./SettingsModal";
import { fs, ink, ringColor, SOFT, T, withAlpha } from "./tokens";
import { DimensionKey, LifeAreasConfig, RingInsight, RingNextStep, RingSnapshot } from "./types";

const REFILL_DELAY_MS = 2000; // a new step appears 2s after one is completed
const DELTA_MS = 3000; // ±N from the last check-in stays for 3 seconds

type Modal = { kind: "checkin"; key: DimensionKey | null } | { kind: "settings" } | null;

// Everything below is scoped to this card (class "lg2"): Astria UI Master
// tokens, focus ring, hover / press feedback and reduced motion.
const SCOPED_CSS = `
.lg2 { font-family: ${T.font.family}; }
.lg2 :focus-visible { outline: 2px solid ${T.color.primary}; outline-offset: 2px; }
.lg2 .lg2-btn { transition: filter ${T.ms.instant}ms, transform ${T.ms.instant}ms; }
.lg2 .lg2-btn:hover:not(:disabled) { filter: brightness(1.08); }
.lg2 .lg2-btn:active:not(:disabled) { transform: scale(0.97); }
.lg2 .lg2-row { transition: transform ${T.ms.fast}ms ${T.ease.default}, box-shadow ${T.ms.fast}ms ${T.ease.default}; }
.lg2 .lg2-row:hover:not(:disabled) { transform: translateY(-2px); }
.lg2 input[type=range].lg2-range {
  -webkit-appearance: none; appearance: none; width: 100%; height: 6px; border-radius: 9999px; cursor: pointer;
  background: linear-gradient(to right, var(--lg2-fill) var(--lg2-pct), ${T.color.bg.overlay} var(--lg2-pct));
}
.lg2 input[type=range].lg2-range::-webkit-slider-thumb {
  -webkit-appearance: none; width: 20px; height: 20px; border-radius: 50%;
  background: var(--lg2-fill); border: 3px solid #fff; box-shadow: ${T.shadow.md};
}
.lg2 input[type=range].lg2-range::-moz-range-thumb {
  width: 14px; height: 14px; border-radius: 50%; background: var(--lg2-fill); border: 3px solid #fff; box-shadow: ${T.shadow.md};
}
.lg2 input[type=range].lg2-range::-moz-range-track { background: transparent; }
@keyframes lg2-modal-in { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: none; } }
.lg2 .lg2-modal { animation: lg2-modal-in ${T.ms.normal}ms ${T.ease.default}; }
@keyframes lg2-spin { to { transform: rotate(360deg); } }
@keyframes lg2-load-pulse { 0%, 100% { transform: scale(0.94); } 50% { transform: scale(1.06); } }
@keyframes lg2-breathe { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
@keyframes lg2-enter { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
.lg2 .lg2-enter { animation: lg2-enter ${T.ms.normal}ms ${T.ease.default}; }
@keyframes lg2-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
@media (prefers-reduced-motion: reduce) {
  .lg2 *, .lg2 *::before, .lg2 *::after { animation: none !important; transition: none !important; }
}
`;

const cardStyle: React.CSSProperties = {
  background: T.color.bg.surface,
  border: `1px solid ${T.color.bg.overlay}`,
  borderRadius: T.radius.xxl,
  boxShadow: T.shadow.card,
  color: T.color.text.primary,
  ...fs("base"),
};

// The page-enter fade+slide runs on an inner wrapper only: a transformed ancestor
// would become the containing block of the fixed-position dialogs, so those are
// rendered as `overlays`, outside the animated wrapper.
const Shell: React.FC<{
  children: React.ReactNode;
  enter?: boolean;
  overlays?: React.ReactNode;
}> = ({ children, enter, overlays }) => (
  <section
    data-rings-keep
    aria-label={i18n.t("lifeAreas.sectionLabel")}
    className="lg2 mx-auto max-w-3xl p-5 sm:p-8"
    style={cardStyle}
  >
    <style>{SCOPED_CSS}</style>
    <div className={enter ? "lg2-enter" : undefined}>{children}</div>
    {overlays}
  </section>
);

const Loading: React.FC = () => (
  <Shell>
    <LifeAreasSkeleton />
  </Shell>
);

const LifeAreasCard: React.FC<{ initialSnapshot: RingSnapshot }> = ({ initialSnapshot }) => {
  const L = useLifeAreas();
  const { t } = useTranslation();
  const { scale } = L.config;
  const [snapshot, setSnapshot] = useState<RingSnapshot>(initialSnapshot);
  const [reminderHidden, setReminderHidden] = useState(false);
  const [steps, setSteps] = useState<RingNextStep[]>([]);
  const [insights, setInsights] = useState<RingInsight[]>([]);
  const [selected, setSelected] = useState<DimensionKey | null>(null);
  const [modal, setModal] = useState<Modal>(null);
  const [live, setLive] = useState("");
  const [delta, setDelta] = useState<{ label: string; n: number } | null>(null);

  const loadSteps = useCallback(() => {
    getNextSteps()
      .then(setSteps)
      .catch(() => {
        // Steps are secondary; the rings still work without them.
      });
  }, []);
  const loadInsights = useCallback(() => {
    getInsights().then(setInsights).catch(() => {});
  }, []);

  useEffect(() => {
    loadSteps();
    loadInsights();
  }, [loadSteps, loadInsights]);

  useEffect(() => {
    if (!delta) return;
    const id = setTimeout(() => setDelta(null), DELTA_MS);
    return () => clearTimeout(id);
  }, [delta]);

  // Offline check-ins sync as soon as the connection is back.
  useEffect(() => {
    const onOnline = async () => {
      const next = await flushQueue();
      if (next) {
        setSnapshot(next);
        loadSteps();
        loadInsights();
        toast.success(i18n.t("lifeAreas.synced"));
      }
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [loadSteps, loadInsights]);

  // Spec shortcut "1..8": jump to a life area (only while this card is mounted,
  // and never while typing or inside a dialog).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (modal || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable)) return;
      const n = Number(e.key);
      const key = Number.isInteger(n) ? L.areas.find((a) => a.shortcut === n)?.key : undefined;
      if (!key || !snapshot.dimensions.find((d) => d.key === key)?.isActive) return;
      setSelected((cur) => (cur === key ? null : key));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [modal, snapshot, L]);

  // Selecting from the rings brings the matching row into view.
  useEffect(() => {
    if (!selected) return;
    document
      .getElementById(`lg2-area-${selected}`)
      ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selected]);

  // Tapping outside the card clears the selection (the card carries data-rings-keep).
  useEffect(() => {
    if (!selected) return;
    const clear = (e: PointerEvent) => {
      if (!(e.target as Element | null)?.closest("[data-rings-keep]")) setSelected(null);
    };
    document.addEventListener("pointerdown", clear);
    return () => document.removeEventListener("pointerdown", clear);
  }, [selected]);

  const composite = useMemo(() => compositeOf(snapshot), [snapshot]);

  const handleSaved = (next: RingSnapshot, fresh: RingInsight[]) => {
    // ±N for the area that just changed, from its previous score (0-100 scale).
    const changed = next.dimensions.find(
      (d) =>
        d.previousScore != null &&
        d.score != null &&
        d.score !== d.previousScore &&
        d.lastUpdatedAt &&
        Date.now() - new Date(d.lastUpdatedAt).getTime() < 60000,
    );
    if (changed) {
      setDelta({
        label: L.area(changed.key).label,
        n: Math.round(
          ((changed.score as number) - (changed.previousScore as number)) * (scale.max / scale.storageMax),
        ),
      });
    }
    setSnapshot(next);
    setInsights((cur) => [...fresh, ...cur.filter((c) => !fresh.some((f) => f.id === c.id))]);
    loadInsights(); // warnings that just ended are gone server-side
    loadSteps();
    const after = compositeOf(next).score;
    setLive(
      t("lifeAreas.scoreUpdated", {
        score: after == null ? t("lifeAreas.unavailable") : after,
        max: scale.max,
      }),
    );
  };

  const handleAct = async (id: string, action: "complete" | "dismiss") => {
    try {
      await actOnStep(id, action);
      setSteps((cur) => cur.filter((s) => s.id !== id));
      setLive(t(action === "complete" ? "lifeAreas.stepCompleted" : "lifeAreas.stepDismissed"));
      setTimeout(loadSteps, action === "complete" ? REFILL_DELAY_MS : 0);
    } catch (err: any) {
      toast.error(serverErrorMessage(err, "lifeAreas.stepUpdateFailed"));
      loadSteps();
    }
  };

  const rated = snapshot.dimensions.some((d) => d.isActive && d.score != null);
  const dimOf = (k: DimensionKey) => snapshot.dimensions.find((d) => d.key === k);
  const selectedDim = selected ? dimOf(selected) ?? null : null;

  const overlays = (
    <>
      {modal?.kind === "checkin" && (
        <CheckInModal
          snapshot={snapshot}
          initialKey={modal.key}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
          onViewSteps={() =>
            document.getElementById("life-gps-next-steps")?.scrollIntoView({ behavior: "smooth" })
          }
        />
      )}
      {modal?.kind === "settings" && (
        <SettingsModal
          snapshot={snapshot}
          onClose={() => setModal(null)}
          onSaved={(next) => {
            setSnapshot(next);
            loadSteps();
          }}
        />
      )}
    </>
  );

  return (
    <Shell enter overlays={overlays}>
      <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h2 style={{ ...fs("xl", 700), margin: 0 }}>{t("lifeAreas.title")}</h2>
          <p style={{ ...fs("sm"), margin: "2px 0 0", color: T.color.text.secondary }}>
            {t("lifeAreas.subtitle", { count: L.areas.length, min: scale.min, max: scale.max })}
          </p>
        </div>
        <button
          type="button"
          aria-label={t("lifeAreas.settings")}
          className="lg2-btn"
          onClick={() => setModal({ kind: "settings" })}
          style={{
            width: 38,
            height: 38,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: T.radius.full,
            border: `1px solid ${T.color.bg.overlay}`,
            background: T.color.bg.card,
            color: T.color.text.secondary,
            cursor: "pointer",
          }}
        >
          <Settings size={18} />
        </button>
      </header>

      {snapshot.reminder.enabled && snapshot.reminder.due && !reminderHidden && (
        <div
          role="status"
          style={{
            marginTop: 14,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 14px",
            background: withAlpha(T.color.primary, 0.08),
            border: `1px solid ${withAlpha(T.color.primary, 0.3)}`,
            borderRadius: T.radius.xl,
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 20 }}>
            🔔
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ ...fs("sm", 600), display: "block" }}>{t("lifeAreas.reminderTitle")}</span>
            <span style={{ ...fs("xs"), display: "block", color: T.color.text.secondary }}>
              {t("lifeAreas.reminderLast", { ago: timeAgo(snapshot.reminder.lastCheckInAt) })}
            </span>
          </span>
          <Button size="sm" onClick={() => setModal({ kind: "checkin", key: null })}>
            {t("lifeAreas.checkInNow")}
          </Button>
          <button
            type="button"
            aria-label={t("lifeAreas.dismissReminder")}
            onClick={() => setReminderHidden(true)}
            style={{ ...fs("lg"), background: "transparent", border: "none", color: T.color.text.secondary, cursor: "pointer", lineHeight: 1 }}
          >
            ×
          </button>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <ConcentricRingsChart
          snapshot={snapshot}
          composite={composite}
          selectedKey={selected}
          onSelect={setSelected}
        />
      </div>

      <div style={{ marginTop: 8, display: "flex", justifyContent: "center" }}>
        <div style={{ minHeight: 30 }} aria-live="polite">
          {delta && (
            <span
              style={{
                ...fs("sm", 600),
                display: "inline-block",
                padding: "3px 14px",
                borderRadius: T.radius.full,
                color: ink(delta.n > 0 ? SOFT.up : SOFT.down),
                background: withAlpha(delta.n > 0 ? SOFT.up : SOFT.down, 0.16),
                border: `1px solid ${withAlpha(delta.n > 0 ? SOFT.up : SOFT.down, 0.5)}`,
              }}
            >
              {delta.n > 0 ? "+" : ""}
              {delta.n} {delta.label}
            </span>
          )}
        </div>
      </div>

      {!rated && (
        <div
          style={{
            marginTop: 20,
            padding: 20,
            textAlign: "center",
            background: T.color.bg.card,
            border: `1px dashed ${T.color.bg.overlay}`,
            borderRadius: T.radius.xl,
          }}
        >
          <div style={{ fontSize: 32 }} aria-hidden="true">
            🌐
          </div>
          <p style={{ ...fs("md", 600), margin: "6px 0 0" }}>{t("lifeAreas.emptyTitle")}</p>
          <p style={{ ...fs("sm"), margin: "4px 0 14px", color: T.color.text.secondary }}>
            {t("lifeAreas.emptyBody")}
          </p>
          <Button onClick={() => setModal({ kind: "checkin", key: null })}>{t("lifeAreas.startCheckIn")}</Button>
        </div>
      )}

      {/* the three spec rings, each with its areas */}
      {L.config.rings.map((ring, ringIdx) => {
        const avg = composite.rings.find((r) => r.id === ring.id);
        const accent = ringColor(ringIdx);
        const avgShown = avg?.avg == null ? null : Math.round(avg.avg);
        const avgBand = L.bandFor(avgShown);
        return (
          <section key={ring.id} aria-label={ring.label} style={{ marginTop: 36 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
              <div style={{ minWidth: 0 }}>
                <h3 style={{ ...fs("base", 600), margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    aria-hidden="true"
                    style={{ width: 10, height: 10, borderRadius: T.radius.full, background: accent }}
                  />
                  {ring.label}
                </h3>
                <p style={{ ...fs("sm"), margin: "4px 0 0", color: T.color.text.secondary }}>{ring.description}</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                {avgBand && (
                  <Badge
                    size="md"
                    color={avgBand.color}
                    title={t("lifeAreas.ringWeight", { pct: Math.round(ring.weight * 100) })}
                  >
                    {avgBand.label}
                  </Badge>
                )}
                {L.config.groups.showScore && avgShown != null && (
                  <span
                    style={{ ...fs("xs"), color: T.color.text.secondary }}
                    aria-label={t("lifeAreas.ringAverage", { ring: ring.label })}
                  >
                    {avgShown}
                  </span>
                )}
              </div>
            </div>
            <ul style={{ margin: 0, padding: 0, display: "grid", gap: 14 }}>
              {ring.areas.map((k) => {
                const d = dimOf(k);
                if (!d) return null;
                const on = selected === k;
                return (
                  <AreaRow key={k} dim={d} selected={on} onToggle={() => setSelected(on ? null : k)}>
                    {on && selectedDim && (
                      <AreaDetail
                        dim={selectedDim}
                        snapshot={snapshot}
                        insight={insights.find((i) => i.dimensionKey === k)}
                        onEdit={() => setModal({ kind: "checkin", key: k })}
                        onSelectArea={setSelected}
                      />
                    )}
                  </AreaRow>
                );
              })}
            </ul>
          </section>
        );
      })}

      <InsightPanel insights={insights} onSelectArea={setSelected} />

      <NextSteps steps={steps} snapshot={snapshot} onAct={handleAct} onCreated={loadSteps} />

      <div style={{ marginTop: 24 }}>
        <Button
          block
          size="lg"
          onClick={() => setModal({ kind: "checkin", key: selectedDim?.isActive ? selectedDim.key : null })}
        >
          {t("lifeAreas.updateGps")}
        </Button>
      </div>

      <span className="sr-only" aria-live="polite">
        {live}
      </span>
    </Shell>
  );
};

// Loads the config and the first snapshot together, then renders the card. The
// card itself holds no copy of the areas, rings, bands, labels or moods.
const LifeRingsSection: React.FC = () => {
  const [boot, setBoot] = useState<{ config: LifeAreasConfig; snapshot: RingSnapshot } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      // Anything saved while offline goes up first so the snapshot is current.
      if (hasQueuedCheckIns()) await flushQueue();
      const [config, snapshot] = await Promise.all([getConfig(), getSnapshot()]);
      setBoot({ config, snapshot });
    } catch (err: any) {
      setError(serverErrorMessage(err, "lifeAreas.loadFailed"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error && !boot) {
    return (
      <Shell>
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <p style={{ ...fs("sm"), color: ink(T.color.semantic.error), margin: "0 0 12px" }}>{error}</p>
          <Button onClick={load}>{i18n.t("lifeGps.page.tryAgain")}</Button>
        </div>
      </Shell>
    );
  }
  if (!boot) return <Loading />;
  return (
    <LifeAreasProvider config={boot.config}>
      <LifeAreasCard initialSnapshot={boot.snapshot} />
    </LifeAreasProvider>
  );
};

export default LifeRingsSection;
